/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

const TEST_IMAGE = {
  URL: '/assets/downloadFileTest.png',
  SIZE_BYTES: 414000, // Approximate size of the image in bytes
};

/**
 * Load an image to measure download speed
 * @param url
 * @returns {Promise<void>}
 */
export async function loadImage(url: string) {
  return new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image at ${url}`));
    img.src = `${url}?nocache=${Math.random()}`; // Add a random query parameter to prevent caching
  });
}

/**
 * Measure download speed by downloading a test image
 * @param testImageUrl - URL of the test image
 * @param testImageSizeBytes - Size of the test image in bytes
 * @returns {Promise<number>} - Returns download speed in Mbps
 */
export async function measureDownloadSpeed(testImageUrl: string, testImageSizeBytes: number) {
  const startTime = performance.now();
  await loadImage(testImageUrl);
  const endTime = performance.now();

  const duration = (endTime - startTime) / 1000; // Convert to seconds
  const speedMbps = (testImageSizeBytes * 8) / duration / 1_000_000; // Convert to Mbps
  return speedMbps;
}

/**
 * Measure average latency by sending requests to the server
 * @param samples - Number of samples to take
 * @returns {Promise<number>} - Returns average latency in milliseconds
 */
export async function measureAverageLatency(samples = 3) {
  // Create an array of promises for concurrent requests
  const latencyPromises = Array.from({ length: samples }, async () => {
    const startPing = performance.now();
    await fetch('/', { method: 'HEAD' });
    const endPing = performance.now();
    return endPing - startPing; // Return latency for this request
  });

  const latencies = await Promise.all(latencyPromises);

  // Calculate average latency
  const totalLatency = latencies.reduce((sum, val) => sum + val, 0);
  return totalLatency / samples;
}

/**
 * Run a speed and latency test
 * @param {string} testImageUrl - The URL of the test image to download for measuring speed
 * @param {number} testImageSizeBytes - The size of the test image in bytes
 * @returns {Promise<{downloadSpeed: string, latency: string}>} - The measured download speed (Mbps) and latency (ms)
 */
export async function runSpeedAndLatencyTest(
  testImageUrl = TEST_IMAGE.URL,
  testImageSizeBytes = TEST_IMAGE.SIZE_BYTES,
) {
  try {
    // Validate the image URL
    if (!testImageUrl || typeof testImageUrl !== 'string') {
      throw new Error('Invalid test image URL provided');
    }

    const downloadSpeed = await measureDownloadSpeed(testImageUrl, testImageSizeBytes);
    const latency = await measureAverageLatency();

    return {
      downloadSpeed: downloadSpeed.toFixed(2),
      latency: latency.toFixed(2),
    };
  } catch (err: unknown) {
    // eslint-disable-next-line no-console
    console.error('[ZOOM] Speed and Latency Test failed:', err);
    throw new Error('Network test failed');
  }
}
